/**
 * CommandersManager — CRUD des accès commandants (section Settings › Commandants)
 *
 * - Liste réelle via GET /api/commanders
 * - Créer / Éditer / Supprimer via POST/PATCH/DELETE /api/commanders/:id
 * - Stats calculées depuis la liste (plus de valeurs figées)
 * - Chaque écriture est auditée côté API (audit_log catégorie access_control)
 *
 * Sécurité : l'authentification réelle (session CF Access) reste gérée au bord
 * Cloudflare Zero Trust ; cette UI gère le RBAC/annuaire des commandants, pas
 * l'émission de sessions. Le rôle provient de l'enum partagé CommanderRole.
 */

import { useState, useEffect, useCallback } from "react";
import type { Commander, CommanderRole } from "@synapse/shared";
import { api } from "../lib/api";
import { showToast } from "./Toast";

const ROLES: { value: CommanderRole; label: string; icon: string; desc: string }[] = [
  { value: "admin", label: "Admin", icon: "🛡", desc: "Accès total · gestion des accès" },
  { value: "ops", label: "Ops", icon: "🎛", desc: "Opérations · missions · déploiements" },
  { value: "viewer", label: "Viewer", icon: "👁", desc: "Lecture seule" },
];

const ROLE_META: Record<string, { label: string; icon: string; color: string }> = {
  admin: { label: "Admin", icon: "🛡", color: "var(--rust)" },
  ops: { label: "Ops", icon: "🎛", color: "var(--blue)" },
  viewer: { label: "Viewer", icon: "👁", color: "var(--muted)" },
};

const AVATARS = ["👤", "🧑‍🚀", "👨‍💻", "👩‍💻", "🧙", "🛡", "🦉", "🐺", "🦅", "🚀"];

function relTime(iso?: string): string {
  if (!iso) return "jamais";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const diff = Date.now() - d;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return `il y a ${j} j`;
}

function isActiveSession(iso?: string): boolean {
  if (!iso) return false;
  const d = new Date(iso).getTime();
  return !Number.isNaN(d) && Date.now() - d < 30 * 60 * 1000; // < 30 min
}

interface FormState {
  id?: string;
  email: string;
  name: string;
  role: CommanderRole;
  avatar: string;
  accessWebUI: boolean;
  accessDiscord: boolean;
}

const EMPTY_FORM: FormState = {
  email: "",
  name: "",
  role: "viewer",
  avatar: "👤",
  accessWebUI: true,
  accessDiscord: false,
};

export default function CommandersManager({ cfDomain }: { cfDomain: string }) {
  const [list, setList] = useState<Commander[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState<FormState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Commander | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.commanders.list();
      setList(data);
    } catch {
      setList([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = {
    total: list.length,
    active: list.filter((c) => isActiveSession(c.lastActiveAt)).length,
    admins: list.filter((c) => c.role === "admin").length,
    webui: list.filter((c) => c.accessWebUI).length,
  };
  // MFA/SSO : appliqué au bord par Cloudflare Zero Trust pour tous les accès WebUI
  const mfaPct = cfDomain && stats.webui > 0 ? "100%" : "—";

  const doDelete = async (c: Commander) => {
    try {
      await api.commanders.delete(c.id);
      showToast("success", "Commandant supprimé", `${c.name} n'a plus accès.`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      showToast("error", "Échec suppression", err instanceof Error ? err.message : "Erreur");
    }
  };

  return (
    <>
      {/* Stats calculées réelles */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="eyebrow">👑 commandants</div>
          <div className="val">{loaded ? stats.total : "—"}</div>
        </div>
        <div className="stat-card">
          <div className="eyebrow">🟢 sessions actives</div>
          <div className="val" style={{ color: "var(--blue)" }}>{loaded ? stats.active : "—"}</div>
        </div>
        <div className="stat-card">
          <div className="eyebrow">🛡 admins</div>
          <div className="val" style={{ color: "var(--rust)" }}>{loaded ? stats.admins : "—"}</div>
        </div>
        <div className="stat-card">
          <div className="eyebrow">🔐 MFA (CF ZT)</div>
          <div className="val" style={{ color: "var(--olive)" }}>{mfaPct}</div>
        </div>
      </div>

      {/* Table de gestion des accès */}
      <div className="settings-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <h3 style={{ margin: 0 }}>👑 Accès commandants</h3>
            <span style={{ fontSize: "11px", color: "var(--muted)" }}>Annuaire &amp; rôles (RBAC) — chaque changement est audité</span>
          </div>
          <button className="btn primary" style={{ fontSize: "12px", padding: "9px 15px" }} onClick={() => setModal({ ...EMPTY_FORM })}>
            ➕ Nouveau commandant
          </button>
        </div>

        {/* Légende des rôles */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {ROLES.map((r) => {
            const rm = ROLE_META[r.value];
            return (
              <span key={r.value} title={r.desc} style={{
                fontSize: "10px", fontWeight: 600, padding: "3px 9px", borderRadius: "999px",
                background: `${rm.color}14`, color: rm.color, display: "inline-flex", alignItems: "center", gap: "4px",
              }}>{rm.icon} {rm.label}</span>
            );
          })}
        </div>

        {!loaded && <div className="no-results">Chargement…</div>}
        {loaded && list.length === 0 && (
          <div style={{
            padding: "36px 24px", textAlign: "center", borderRadius: "16px",
            background: "rgba(0,0,0,0.14)", border: "1px dashed var(--line)",
          }}>
            <div style={{ fontSize: "34px", marginBottom: "8px", opacity: 0.7 }}>👑</div>
            <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "4px" }}>Aucun commandant</div>
            <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "14px" }}>
              L'annuaire des accès est vide. Créez le premier commandant pour l'amorcer.
            </div>
            <button className="btn primary" style={{ fontSize: "12px", padding: "9px 15px" }} onClick={() => setModal({ ...EMPTY_FORM })}>
              ➕ Créer le premier accès
            </button>
          </div>
        )}

        {loaded && list.length > 0 && (
          <div style={{ display: "grid", gap: "8px" }}>
            {/* En-têtes */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "auto 1.6fr 1fr 0.8fr 1fr auto",
              gap: "12px", padding: "0 14px", fontSize: "10px",
              textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)",
            }}>
              <span></span>
              <span>Identité</span>
              <span>Rôle</span>
              <span>Accès</span>
              <span>Dernière activité</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>

            {list.map((c) => {
              const rm = ROLE_META[c.role] ?? ROLE_META.viewer;
              const online = isActiveSession(c.lastActiveAt);
              return (
                <div key={c.id} className="commander-row" style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1.6fr 1fr 0.8fr 1fr auto",
                  gap: "12px", alignItems: "center",
                  padding: "12px 14px", borderRadius: "14px",
                  background: "rgba(0,0,0,0.16)", border: "1px solid var(--line)",
                  borderLeft: `3px solid ${rm.color}`,
                  transition: "border-color 0.15s, background 0.15s",
                }}>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "12px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "21px", background: `${rm.color}16`,
                    }}>{c.avatar || "👤"}</div>
                    {online && (
                      <span style={{
                        position: "absolute", bottom: "-1px", right: "-1px", width: "11px", height: "11px",
                        borderRadius: "999px", background: "var(--olive)", border: "2px solid #121629",
                        boxShadow: "0 0 5px var(--olive)",
                      }} />
                    )}
                  </div>

                  <div style={{ display: "grid", gap: "2px", minWidth: 0 }}>
                    <strong style={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</strong>
                    <span style={{ fontSize: "11px", color: "var(--muted)", fontFamily: "var(--mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</span>
                  </div>

                  <div>
                    <span style={{
                      fontSize: "11px", fontWeight: 600, padding: "4px 10px",
                      borderRadius: "999px", background: `${rm.color}1e`, color: rm.color,
                      display: "inline-flex", alignItems: "center", gap: "5px",
                    }}>{rm.icon} {rm.label}</span>
                  </div>

                  <div style={{ display: "flex", gap: "6px" }}>
                    <span title={c.accessWebUI ? "Accès WebUI activé" : "Pas d'accès WebUI"} style={{
                      fontSize: "13px", width: "26px", height: "26px", borderRadius: "8px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: c.accessWebUI ? "rgba(94,199,168,0.12)" : "rgba(255,255,255,0.03)",
                      opacity: c.accessWebUI ? 1 : 0.35,
                    }}>🖥</span>
                    <span title={c.accessDiscord ? "Accès Discord activé" : "Pas d'accès Discord"} style={{
                      fontSize: "13px", width: "26px", height: "26px", borderRadius: "8px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: c.accessDiscord ? "rgba(107,141,214,0.14)" : "rgba(255,255,255,0.03)",
                      opacity: c.accessDiscord ? 1 : 0.35,
                    }}>💬</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: online ? "var(--olive)" : "var(--muted)", fontFamily: "var(--mono)" }}>
                    <span style={{
                      width: "7px", height: "7px", borderRadius: "999px",
                      background: online ? "var(--olive)" : "rgba(255,255,255,0.15)",
                    }} />
                    {relTime(c.lastActiveAt)}
                  </div>

                  <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                    <button className="btn" style={{ fontSize: "11px", padding: "6px 10px" }} title="Éditer"
                      onClick={() => setModal({
                        id: c.id, email: c.email, name: c.name, role: c.role as CommanderRole,
                        avatar: c.avatar || "👤", accessWebUI: c.accessWebUI, accessDiscord: c.accessDiscord,
                      })}>✎</button>
                    <button className="btn" style={{ fontSize: "11px", padding: "6px 10px", color: "var(--rust)" }} title="Supprimer"
                      onClick={() => setConfirmDelete(c)}>🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`.commander-row:hover{border-color:rgba(255,255,255,0.18)!important;background:rgba(255,255,255,0.03)!important;}`}</style>

      {modal && (
        <CommanderModal
          form={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          commander={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => doDelete(confirmDelete)}
        />
      )}
    </>
  );
}

// ── Modal créer / éditer ────────────────────────────────────────

function CommanderModal({ form, onClose, onSaved }: {
  form: FormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!form.id;
  const [email, setEmail] = useState(form.email);
  const [name, setName] = useState(form.name);
  const [role, setRole] = useState<CommanderRole>(form.role);
  const [avatar, setAvatar] = useState(form.avatar);
  const [accessWebUI, setAccessWebUI] = useState(form.accessWebUI);
  const [accessDiscord, setAccessDiscord] = useState(form.accessDiscord);
  const [saving, setSaving] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    if (!name.trim()) { showToast("error", "Nom requis", "Renseigne un nom."); return; }
    if (!emailValid) { showToast("error", "E-mail invalide", "Renseigne un e-mail valide."); return; }
    setSaving(true);
    try {
      if (isEdit && form.id) {
        await api.commanders.update(form.id, {
          email: email.trim(), name: name.trim(), role, avatar, accessWebUI, accessDiscord,
        });
        showToast("success", "Commandant mis à jour", `${name.trim()} enregistré.`);
      } else {
        await api.commanders.create({
          email: email.trim(), name: name.trim(), role, avatar, accessWebUI, accessDiscord,
        });
        showToast("success", "Commandant créé", `${name.trim()} (${role}) ajouté.`);
      }
      onSaved();
    } catch (err) {
      showToast("error", "Échec", err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "480px", maxWidth: "94vw", background: "#121629", border: "1px solid var(--line)",
        borderRadius: "20px", padding: "22px", display: "grid", gap: "14px",
        boxShadow: "0 24px 70px rgba(0,0,0,0.5)", maxHeight: "90vh", overflow: "auto",
      }}>
        <strong style={{ fontSize: "17px" }}>{isEdit ? "✎ Éditer le commandant" : "➕ Nouveau commandant"}</strong>

        <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
          Nom complet
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Jonathan Johnson" />
        </label>

        <label style={{ display: "grid", gap: "4px", fontSize: "12px" }}>
          E-mail
          <input className="field-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@technosecur.com"
            style={{ borderColor: email && !emailValid ? "var(--rust)" : undefined }} />
        </label>

        <div style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
          Rôle
          <div style={{ display: "grid", gap: "6px" }}>
            {ROLES.map((r) => (
              <div key={r.value} onClick={() => setRole(r.value)} style={{
                display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px",
                borderRadius: "12px", cursor: "pointer",
                background: role === r.value ? "rgba(183,148,246,0.14)" : "rgba(0,0,0,0.14)",
                border: `1px solid ${role === r.value ? "rgba(183,148,246,0.3)" : "var(--line)"}`,
              }}>
                <span style={{ fontSize: "18px" }}>{r.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: "11px", color: "var(--muted)" }}>{r.desc}</div>
                </div>
                <span style={{
                  width: "18px", height: "18px", borderRadius: "999px",
                  border: `2px solid ${role === r.value ? "var(--olive)" : "var(--line)"}`,
                  background: role === r.value ? "var(--olive)" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11px", color: "white",
                }}>{role === r.value ? "✓" : ""}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
          Avatar
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {AVATARS.map((a) => (
              <span key={a} onClick={() => setAvatar(a)} style={{
                width: "36px", height: "36px", borderRadius: "10px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
                background: avatar === a ? "rgba(183,148,246,0.18)" : "rgba(0,0,0,0.14)",
                border: `1px solid ${avatar === a ? "rgba(183,148,246,0.3)" : "var(--line)"}`,
              }}>{a}</span>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: "8px", fontSize: "12px" }}>
          Accès
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input type="checkbox" checked={accessWebUI} onChange={(e) => setAccessWebUI(e.target.checked)} />
            🖥 WebUI (console Synapse)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input type="checkbox" checked={accessDiscord} onChange={(e) => setAccessDiscord(e.target.checked)} />
            💬 Discord (notifications & commandes)
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
          <button className="btn" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn primary" onClick={submit} disabled={saving}>
            {saving ? "⟳ Enregistrement…" : isEdit ? "Enregistrer" : "Créer l'accès"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de confirmation de suppression ────────────────────────

function ConfirmDeleteModal({ commander, onCancel, onConfirm }: {
  commander: Commander;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001, padding: "24px",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "400px", maxWidth: "94vw", background: "#121629", border: "1px solid var(--rust)",
        borderRadius: "20px", padding: "22px", display: "grid", gap: "14px",
        boxShadow: "0 24px 70px rgba(0,0,0,0.5)",
      }}>
        <strong style={{ fontSize: "16px" }}>🗑 Supprimer l'accès</strong>
        <p style={{ fontSize: "13px", color: "var(--muted)", margin: 0, lineHeight: 1.5 }}>
          Confirmer la suppression de l'accès de <strong style={{ color: "#fff" }}>{commander.name}</strong> ({commander.email}) ?
          Cette action révoque immédiatement ses droits et est journalisée dans l'audit.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <button className="btn" onClick={onCancel}>Annuler</button>
          <button className="btn" style={{ background: "var(--rust)", color: "#fff", borderColor: "var(--rust)" }} onClick={onConfirm}>
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
}
